import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { getDashboardStats, getDirectionDashboardStats, getOperationalCases, getProducts, getBonusTracking, syncVposComprehensive, syncShopifyCustomers, getProjectedRevenue } from '../lib/api';
import { formatCurrency, movementTypeLabels } from '../lib/utils';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Target, DollarSign, CreditCard, Package, Loader2,
  BarChart3, PieChart, Activity, Award, ShoppingBag, ArrowUpRight, Store,
  RefreshCw, CheckCircle, Clock, AlertTriangle, XCircle, ShoppingCart, CalendarDays, CheckSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const DashboardDireccion = () => {
  const navigate = useNavigate();
  const { isDirection } = useAuth();
  const [stats, setStats] = useState(null);
  const [dirStats, setDirStats] = useState(null);
  const [bonusData, setBonusData] = useState(null);
  const [movements, setMovements] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingVpos, setSyncingVpos] = useState(false);
  const [syncingShopify, setSyncingShopify] = useState(false);
  const [projected, setProjected] = useState(null);
  const [loadingProjection, setLoadingProjection] = useState(false);

  useEffect(() => { fetchData(); fetchProjection(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, dirStatsRes, movementsRes, productsRes, bonusRes] = await Promise.all([
        getDashboardStats(),
        getDirectionDashboardStats(),
        getOperationalCases({}),
        getProducts(true),
        getBonusTracking()
      ]);
      setStats(statsRes.data);
      setDirStats(dirStatsRes.data);
      setMovements(movementsRes.data.slice(0, 10));
      setLowStockProducts(productsRes.data);
      setBonusData(bonusRes.data);
    } catch (error) {
      
    } finally { setLoading(false); }
  };

  const fetchProjection = async () => {
    setLoadingProjection(true);
    try {
      const res = await getProjectedRevenue();
      if (!res.data.error) setProjected(res.data);
    } catch (e) { console.error('Error cargando dashboard:', e.message); }
    finally { setLoadingProjection(false); }
  };

  const handleSyncVpos = async () => {
    setSyncingVpos(true);
    try {
      const res = await syncVposComprehensive();
      if (res.data.error) { toast.error(res.data.error); }
      else {
        toast.success('Virtual POS: sincronizacion iniciada. Se actualizara en ~60s');
        setTimeout(() => fetchData(), 65000); // Refresh after sync completes
      }
    } catch { toast.error('Error al sincronizar VPos'); }
    finally { setSyncingVpos(false); }
  };

  const handleSyncShopify = async () => {
    setSyncingShopify(true);
    try {
      // Sync both orders and customers in parallel
      const [ordersRes, customersRes] = await Promise.all([
        api.post('/shopify-orders/sync'),
        syncShopifyCustomers()
      ]);
      const newOrders = ordersRes.data.new_orders || 0;
      const newLeads = customersRes.data.nuevos || 0;
      toast.success(`Shopify: ${newOrders} ordenes nuevas, ${newLeads} leads nuevos`);
      fetchData();
    } catch { toast.error('Error al sincronizar Shopify'); }
    finally { setSyncingShopify(false); }
  };

  if (!isDirection()) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <h2 className="text-xl font-semibold text-[#1A2835]">Acceso Restringido</h2>
          <p className="text-[#5C7083]">Esta seccion es solo para Direccion</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="dashboard-direccion-loading"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  const vpos = dirStats?.virtualpos || {};
  const pat = vpos.pat || {};
  const shopifyOrders = dirStats?.shopify_orders || {};
  const presencial = dirStats?.presencial || {};
  const monthlyGoal = 5000000;
  const goalProgress = ((stats?.month_sales || 0) / monthlyGoal) * 100;
  const maxPlanCount = dirStats?.plan_distribution?.length > 0 ? Math.max(...dirStats.plan_distribution.map(p => p.count)) : 1;
  const maxTrendVal = dirStats?.monthly_trends?.length > 0 ? Math.max(...dirStats.monthly_trends.flatMap(t => [t.altas, t.bajas]), 1) : 1;
  const planColors = ['#2B4257', '#4A7C59', '#D98A6C', '#C25953', '#5C7083', '#B0C2D1', '#3D5A75', '#E8E2D7', '#8B6F47', '#6B8E7B'];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-direccion">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Dashboard Direccion</h1>
          <p className="page-subtitle">Vista ejecutiva - 3 canales de ingreso</p>
        </div>
        <div className="flex gap-2">
          <Button className="btn-secondary gap-2 text-sm" onClick={handleSyncVpos} disabled={syncingVpos} data-testid="btn-sync-vpos-dash">
            {syncingVpos ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Sync VPos
          </Button>
          <Button className="btn-secondary gap-2 text-sm" onClick={handleSyncShopify} disabled={syncingShopify} data-testid="btn-sync-shopify-dash">
            {syncingShopify ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
            Sync Shopify
          </Button>
        </div>
      </div>

      {/* ====== CANAL 1: VIRTUAL POS (Suscripciones) ====== */}
      <div className="space-y-4" data-testid="channel-vpos">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#2B4257] rounded-lg"><CreditCard className="text-white" size={18} /></div>
          <div>
            <h2 className="text-lg font-bold text-[#1A2835]">Canal 1: Virtual POS (Suscripciones)</h2>
            {vpos.last_sync && <p className="text-xs text-[#5C7083]">Ultimo sync: {new Date(vpos.last_sync).toLocaleString('es-CL')}</p>}
          </div>
        </div>

        {/* VPos KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 border-l-4 border-l-[#4A7C59]" data-testid="vpos-ventas-dia">
            <p className="text-xs text-[#5C7083] mb-1">Ventas del Dia</p>
            <p className="text-xl font-bold text-[#4A7C59]">{formatCurrency(vpos.ventas_dia || 0)}</p>
          </div>
          <div className="card p-4 border-l-4 border-l-[#2B4257]" data-testid="vpos-ventas-mes">
            <p className="text-xs text-[#5C7083] mb-1">Total Cobrado Mes (Bruto)</p>
            <p className="text-xl font-bold text-[#2B4257]">{formatCurrency(vpos.total_cobrado_mes || vpos.ventas_mes || 0)}</p>
            <p className="text-[10px] text-[#5C7083]">{pat.autorizado?.cantidad || 0} cargos exitosos</p>
          </div>
          <div className="card p-4 border-l-4 border-l-[#4A7C59]" data-testid="vpos-subs-activas">
            <p className="text-xs text-[#5C7083] mb-1">Suscripciones Activas</p>
            <p className="text-xl font-bold text-[#4A7C59]">{vpos.suscripciones_activas || 0}</p>
            <p className="text-[10px] text-[#5C7083]">Planes vigentes</p>
          </div>
          <div className="card p-4 border-l-4 border-l-[#C25953]" data-testid="vpos-rechazados-kpi">
            <p className="text-xs text-[#5C7083] mb-1">Rechazados</p>
            <p className="text-xl font-bold text-[#C25953]">{pat.rechazado?.cantidad || 0}</p>
            <p className="text-[10px] text-[#5C7083]">{formatCurrency(pat.rechazado?.monto || 0)}</p>
          </div>
        </div>

        {/* PAT Dashboard */}
        <div className="card" data-testid="vpos-pat-dashboard">
          <h3 className="text-base font-semibold text-[#1A2835] mb-4">Dashboard PAT - Estado de Cargos del Mes</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-[#EDF5F0] rounded-xl text-center">
              <CheckCircle className="mx-auto text-[#4A7C59] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#4A7C59]">{pat.autorizado?.cantidad || 0}</p>
              <p className="text-xs text-[#5C7083] mt-1">Autorizados</p>
              <p className="text-sm font-semibold text-[#4A7C59]">{formatCurrency(pat.autorizado?.monto || 0)}</p>
            </div>
            <div className="p-4 bg-[#FFF8E1] rounded-xl text-center">
              <Clock className="mx-auto text-[#D98A6C] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#D98A6C]">{pat.en_proceso?.cantidad || 0}</p>
              <p className="text-xs text-[#5C7083] mt-1">En Proceso</p>
              <p className="text-sm font-semibold text-[#D98A6C]">{formatCurrency(pat.en_proceso?.monto || 0)}</p>
            </div>
            <div className="p-4 bg-[#F5F0E8] rounded-xl text-center">
              <AlertTriangle className="mx-auto text-[#5C7083] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#5C7083]">{pat.pendiente?.cantidad || 0}</p>
              <p className="text-xs text-[#5C7083] mt-1">Pendientes</p>
              <p className="text-sm font-semibold text-[#5C7083]">{formatCurrency(pat.pendiente?.monto || 0)}</p>
            </div>
            <div className="p-4 bg-[#FBEAE9] rounded-xl text-center">
              <XCircle className="mx-auto text-[#C25953] mb-2" size={24} />
              <p className="text-2xl font-bold text-[#C25953]">{pat.rechazado?.cantidad || 0}</p>
              <p className="text-xs text-[#5C7083] mt-1">Rechazados</p>
              <p className="text-sm font-semibold text-[#C25953]">{formatCurrency(pat.rechazado?.monto || 0)}</p>
            </div>
          </div>
        </div>

        {/* Suscripciones resumen */}
        <div className="grid grid-cols-3 gap-4">
          <div className="card p-4 text-center" data-testid="vpos-subs-activas">
            <p className="text-3xl font-bold text-[#4A7C59]">{vpos.suscripciones_activas || 0}</p>
            <p className="text-xs text-[#5C7083]">Suscripciones Activas</p>
          </div>
          <div className="card p-4 text-center" data-testid="vpos-subs-canceladas">
            <p className="text-3xl font-bold text-[#C25953]">{vpos.suscripciones_canceladas || 0}</p>
            <p className="text-xs text-[#5C7083]">Canceladas</p>
          </div>
          <div className="card p-4 text-center" data-testid="vpos-subs-total">
            <p className="text-3xl font-bold text-[#2B4257]">{vpos.suscripciones_totales || 0}</p>
            <p className="text-xs text-[#5C7083]">Total Historico</p>
          </div>
        </div>
      </div>

      {/* ====== CANAL 2: SHOPIFY (Online) ====== */}
      <div className="space-y-4" data-testid="channel-shopify">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#4A7C59] rounded-lg"><ShoppingBag className="text-white" size={18} /></div>
          <h2 className="text-lg font-bold text-[#1A2835]">Canal 2: Shopify (Online)</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="card p-4 border-l-4 border-l-[#4A7C59]" data-testid="shopify-revenue">
            <p className="text-xs text-[#5C7083] mb-1">Ventas Mensuales</p>
            <p className="text-xl font-bold text-[#4A7C59]">{formatCurrency(shopifyOrders.revenue_month || 0)}</p>
            <p className="text-xs text-[#5C7083]">{shopifyOrders.this_month || 0} ordenes</p>
          </div>
          <div className="card p-4 border-l-4 border-l-[#2B4257]" data-testid="shopify-total-orders">
            <p className="text-xs text-[#5C7083] mb-1">Total Ordenes</p>
            <p className="text-xl font-bold text-[#2B4257]">{shopifyOrders.total || 0}</p>
            <p className="text-xs text-[#5C7083]">Historico</p>
          </div>
          <div className="card p-4 border-l-4 border-l-[#D98A6C]" data-testid="shopify-leads">
            <p className="text-xs text-[#5C7083] mb-1">Leads Shopify</p>
            <p className="text-xl font-bold text-[#D98A6C]">{dirStats?.shopify?.total_leads || 0}</p>
            <p className="text-xs text-[#5C7083]">Tasa conversion: {dirStats?.shopify?.conversion_rate || 0}%</p>
          </div>
        </div>

        {/* Product Type Breakdown */}
        {shopifyOrders.product_breakdown?.length > 0 && (
          <div className="card" data-testid="shopify-product-breakdown">
            <h3 className="text-base font-semibold text-[#1A2835] mb-4">Desglose por Tipo de Producto</h3>
            <div className="space-y-3">
              {shopifyOrders.product_breakdown.map((p, i) => (
                <div key={p.type} className="flex items-center justify-between p-3 bg-[#F5F0E8] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: planColors[i % planColors.length] }} />
                    <span className="text-sm font-medium text-[#1A2835]">{p.type}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold" style={{ color: planColors[i % planColors.length] }}>{p.count}</span>
                    <span className="text-xs text-[#5C7083] ml-2">{formatCurrency(p.revenue || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ====== CANAL 3: PRESENCIAL (Tienda) ====== */}
      <div className="space-y-4" data-testid="channel-presencial">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#D98A6C] rounded-lg"><Store className="text-white" size={18} /></div>
          <h2 className="text-lg font-bold text-[#1A2835]">Canal 3: Ventas Tienda (Presencial)</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 border-l-4 border-l-[#D98A6C]" data-testid="presencial-total">
            <p className="text-xs text-[#5C7083] mb-1">Total Ventas Mes</p>
            <p className="text-xl font-bold text-[#D98A6C]">{formatCurrency(presencial.total || 0)}</p>
          </div>
          {Object.entries(presencial.items || {}).map(([item, amount]) => (
            <div key={item} className="card p-4" data-testid={`presencial-${item}`}>
              <p className="text-xs text-[#5C7083] mb-1 capitalize">{item}</p>
              <p className="text-lg font-bold text-[#1A2835]">{formatCurrency(amount)}</p>
            </div>
          ))}
        </div>

        {/* Categories breakdown */}
        <div className="card" data-testid="presencial-categories">
          <h3 className="text-base font-semibold text-[#1A2835] mb-3">Desglose por Categoria</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(presencial.categories || {}).filter(([, v]) => v > 0).map(([cat, amount]) => (
              <div key={cat} className="p-3 bg-[#F5F0E8] rounded-lg flex items-center justify-between">
                <span className="text-sm text-[#1A2835] capitalize">{cat.replace('_', ' ')}</span>
                <span className="text-sm font-bold text-[#2B4257]">{formatCurrency(amount)}</span>
              </div>
            ))}
            {Object.entries(presencial.categories || {}).every(([, v]) => v === 0) && (
              <p className="text-sm text-[#5C7083] col-span-3 text-center py-4">Sin ventas presenciales este mes</p>
            )}
          </div>
        </div>
      </div>

      {/* ====== PROYECCION PROXIMO MES ====== */}
      <div className="space-y-4" data-testid="projection-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#2B4257] rounded-lg"><CalendarDays className="text-white" size={18} /></div>
            <div>
              <h2 className="text-lg font-bold text-[#1A2835]">Proyeccion {projected?.month_label || 'Proximo Mes'}</h2>
              <p className="text-xs text-[#5C7083]">Cargos programados - Suscripciones activas VPos</p>
            </div>
          </div>
          <Button className="btn-secondary gap-2 text-sm" onClick={fetchProjection} disabled={loadingProjection} data-testid="btn-refresh-projection">
            {loadingProjection ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Actualizar
          </Button>
        </div>

        {projected ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card p-5 bg-gradient-to-br from-[#2B4257] to-[#3D5A75] text-white" data-testid="projected-total">
                <p className="text-sm opacity-80 mb-1">Ingreso Bruto</p>
                <p className="text-3xl font-bold">{formatCurrency(projected.total_projected)}</p>
                <p className="text-sm opacity-70 mt-1">{projected.total_charges} cargos programados</p>
              </div>
              <div className="card p-5 bg-gradient-to-br from-[#4A7C59] to-[#5D9A6E] text-white" data-testid="projected-neto">
                <p className="text-sm opacity-80 mb-1">Ingreso Neto</p>
                <p className="text-3xl font-bold">{formatCurrency(projected.total_neto || 0)}</p>
                <p className="text-sm opacity-70 mt-1">-{projected.comision_pct || 4.17}% comision VPos</p>
              </div>
              <div className="card p-5" data-testid="projected-subs">
                <p className="text-xs text-[#5C7083] mb-1">Suscripciones con Cargo</p>
                <p className="text-2xl font-bold text-[#2B4257]">{projected.total_charges}</p>
                <p className="text-xs text-[#5C7083]">de {projected.active_subscriptions} activas</p>
              </div>
              <div className="card p-5" data-testid="projected-avg">
                <p className="text-xs text-[#5C7083] mb-1">Cargo Promedio</p>
                <p className="text-2xl font-bold text-[#4A7C59]">
                  {projected.total_charges > 0 ? formatCurrency(Math.round(projected.total_projected / projected.total_charges)) : '$0'}
                </p>
                <p className="text-xs text-[#5C7083]">Por suscripcion</p>
              </div>
            </div>

            {projected.comparison && (
              <div className="card p-5" data-testid="projected-comparison">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart3 size={16} className="text-[#2B4257]" />
                  <h3 className="text-base font-semibold text-[#1A2835]">Comparativo Mes a Mes</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-[#F9F7F3] rounded-lg p-4">
                    <p className="text-xs text-[#5C7083] mb-1">{projected.comparison.current_month_label} (Mes Actual)</p>
                    <p className="text-xl font-bold text-[#1A2835]">{formatCurrency(projected.comparison.current_total)}</p>
                    <div className="flex gap-3 mt-2 text-xs text-[#5C7083]">
                      <span>Cobrado: {formatCurrency(projected.comparison.current_cobrado)}</span>
                      <span>Pendiente: {formatCurrency(projected.comparison.current_pendiente)}</span>
                    </div>
                    <p className="text-xs text-[#5C7083] mt-1">{projected.comparison.current_charges} suscripciones</p>
                  </div>
                  <div className="bg-[#F9F7F3] rounded-lg p-4">
                    <p className="text-xs text-[#5C7083] mb-1">{projected.month_label} (Proyeccion)</p>
                    <p className="text-xl font-bold text-[#1A2835]">{formatCurrency(projected.total_projected)}</p>
                    <p className="text-xs text-[#5C7083] mt-2">{projected.total_charges} suscripciones</p>
                  </div>
                  <div className="bg-[#F9F7F3] rounded-lg p-4 flex flex-col justify-center items-center">
                    <p className="text-xs text-[#5C7083] mb-2">Variacion</p>
                    <div className="flex items-center gap-2">
                      {projected.comparison.delta_pct >= 0 ? (
                        <TrendingUp size={20} className="text-[#4A7C59]" />
                      ) : (
                        <TrendingDown size={20} className="text-[#C0392B]" />
                      )}
                      <span className={`text-2xl font-bold ${projected.comparison.delta_pct >= 0 ? 'text-[#4A7C59]' : 'text-[#C0392B]'}`}>
                        {projected.comparison.delta_pct > 0 ? '+' : ''}{projected.comparison.delta_pct}%
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${projected.comparison.delta_pct >= 0 ? 'text-[#4A7C59]' : 'text-[#C0392B]'}`}>
                      {projected.comparison.delta_amount >= 0 ? '+' : ''}{formatCurrency(projected.comparison.delta_amount)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {projected.by_plan?.length > 0 && (
              <div className="card" data-testid="projected-by-plan">
                <h3 className="text-base font-semibold text-[#1A2835] mb-4">Desglose por Plan - {projected.month_label}</h3>
                <div className="space-y-2">
                  {projected.by_plan.map((p, i) => (
                    <div key={p.plan} className="flex items-center justify-between p-3 bg-[#F9F7F3] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: planColors[i % planColors.length] }} />
                        <span className="text-sm text-[#1A2835] truncate max-w-[280px]">{p.plan}</span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-sm font-bold" style={{ color: planColors[i % planColors.length] }}>{p.count}</span>
                        <span className="text-xs text-[#5C7083] ml-3">{formatCurrency(p.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : loadingProjection ? (
          <div className="card text-center py-8">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#2B4257] mb-2" />
            <p className="text-sm text-[#5C7083]">Calculando proyeccion (~90s)...</p>
          </div>
        ) : (
          <div className="card text-center py-8">
            <CalendarDays className="mx-auto h-10 w-10 text-[#E8E2D7] mb-2" />
            <p className="text-sm text-[#5C7083]">Haz click en "Actualizar" para calcular la proyeccion</p>
          </div>
        )}
      </div>

      {/* ====== RESUMEN GENERAL ====== */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-[#1A2835]">Resumen General</h2>

        {/* Total 3 Channels */}
        <div className="card bg-gradient-to-r from-[#2B4257] to-[#3D5A75] text-white" data-testid="total-3-channels">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm opacity-80">Virtual POS</p>
              <p className="text-2xl font-bold">{formatCurrency(vpos.ventas_mes || 0)}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Shopify</p>
              <p className="text-2xl font-bold">{formatCurrency(shopifyOrders.revenue_month || 0)}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Presencial</p>
              <p className="text-2xl font-bold">{formatCurrency(presencial.total || 0)}</p>
            </div>
            <div className="border-l border-white/20 pl-4">
              <p className="text-sm opacity-80">Total Ingresos Mes</p>
              <p className="text-3xl font-bold">{formatCurrency((vpos.ventas_mes || 0) + (shopifyOrders.revenue_month || 0) + (presencial.total || 0))}</p>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
          <div className="card stat-card" data-testid="kpi-today-sales">
            <div className="flex items-center justify-between">
              <DollarSign className="text-[#4A7C59]" size={22} />
              <span className="text-xs text-[#5C7083]">Hoy (Caja)</span>
            </div>
            <div className="stat-value">{formatCurrency(stats?.today_sales || 0)}</div>
            <div className="stat-label">Ventas del Dia</div>
          </div>
          <div className="card stat-card" data-testid="kpi-month-sales">
            <div className="flex items-center justify-between">
              <Target className="text-[#D98A6C]" size={22} />
              <span className="text-xs text-[#5C7083]">Este mes (Caja)</span>
            </div>
            <div className="stat-value">{formatCurrency(stats?.month_sales || 0)}</div>
            <div className="stat-label">Ventas Mes Caja</div>
          </div>
          <div className="card stat-card" data-testid="kpi-net-growth">
            <div className="flex items-center justify-between">
              {stats?.net_growth >= 0 ? <TrendingUp className="text-[#4A7C59]" size={22} /> : <TrendingDown className="text-[#C25953]" size={22} />}
              <span className="text-xs text-[#5C7083]">Este mes</span>
            </div>
            <div className={`stat-value ${stats?.net_growth >= 0 ? 'text-[#4A7C59]' : 'text-[#C25953]'}`}>
              {stats?.net_growth >= 0 ? '+' : ''}{stats?.net_growth || 0}
            </div>
            <div className="stat-label">Crecimiento Neto</div>
          </div>
          <div className="card stat-card" data-testid="kpi-plans-sold">
            <div className="flex items-center justify-between">
              <ShoppingCart className="text-[#2B4257]" size={22} />
              <span className="text-xs text-[#5C7083]">Este mes</span>
            </div>
            <div className="stat-value text-[#2B4257]">{dirStats?.total_plans_sold || 0}</div>
            <div className="stat-label">Planes Vendidos</div>
          </div>
        </div>

        {/* Quick Navigation Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 cursor-pointer hover:border-[#2B4257] transition-colors"
            onClick={() => navigate('/tareas')} data-testid="widget-tareas-pendientes">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#F5F0E8] rounded-lg"><CheckSquare className="text-[#5C7083]" size={18} /></div>
              <div>
                <p className="text-xl font-bold text-[#1A2835]">{stats?.pending_tasks || 0}</p>
                <p className="text-xs text-[#5C7083]">Tareas Pendientes</p>
              </div>
            </div>
          </div>
          <div className="card p-4 cursor-pointer hover:border-[#E65100] transition-colors"
            onClick={() => navigate('/clientes?status=activo')} data-testid="widget-planes-vencer">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FFF3E0] rounded-lg"><CalendarDays className="text-[#E65100]" size={18} /></div>
              <div>
                <p className="text-xl font-bold text-[#E65100]">{stats?.expiring_plans || 0}</p>
                <p className="text-xs text-[#5C7083]">Planes por Vencer</p>
              </div>
            </div>
          </div>
          <div className="card p-4 cursor-pointer hover:border-[#C25953] transition-colors"
            onClick={() => navigate('/pagos-rechazados')} data-testid="widget-rechazados">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FBEAE9] rounded-lg"><XCircle className="text-[#C25953]" size={18} /></div>
              <div>
                <p className="text-xl font-bold text-[#C25953]">{stats?.rejected_payments || 0}</p>
                <p className="text-xs text-[#5C7083]">Pagos Rechazados</p>
              </div>
            </div>
          </div>
          <div className="card p-4 cursor-pointer hover:border-[#4A7C59] transition-colors"
            onClick={() => navigate('/leads')} data-testid="widget-leads">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#EDF5F0] rounded-lg"><Users className="text-[#4A7C59]" size={18} /></div>
              <div>
                <p className="text-xl font-bold text-[#4A7C59]">{stats?.new_leads || 0}</p>
                <p className="text-xs text-[#5C7083]">Leads Nuevos</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Retention & Churn */}
      {dirStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="retention-section">
          <div className="card" data-testid="retention-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-[#EDF5F0] rounded-xl"><Activity className="text-[#4A7C59]" size={22} /></div>
              <div>
                <p className="text-sm text-[#5C7083]">Tasa de Retencion</p>
                <p className="text-3xl font-bold text-[#4A7C59]">{dirStats.retention_rate}%</p>
              </div>
            </div>
            <div className="w-full bg-[#E8E2D7] rounded-full h-3">
              <div className="bg-[#4A7C59] h-3 rounded-full transition-all duration-700" style={{ width: `${dirStats.retention_rate}%` }} />
            </div>
            <p className="text-xs text-[#5C7083] mt-2">{dirStats.active_clients} activos de {dirStats.total_clients} totales</p>
          </div>
          <div className="card" data-testid="churn-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-[#FBEAE9] rounded-xl"><TrendingDown className="text-[#C25953]" size={22} /></div>
              <div>
                <p className="text-sm text-[#5C7083]">Tasa de Churn</p>
                <p className="text-3xl font-bold text-[#C25953]">{dirStats.churn_rate}%</p>
              </div>
            </div>
            <div className="w-full bg-[#E8E2D7] rounded-full h-3">
              <div className="bg-[#C25953] h-3 rounded-full transition-all duration-700" style={{ width: `${Math.min(dirStats.churn_rate, 100)}%` }} />
            </div>
            <p className="text-xs text-[#5C7083] mt-2">{dirStats.cancelled_clients} cancelados, {dirStats.frozen_clients} congelados</p>
          </div>
          <div className="card" data-testid="client-summary-card">
            <p className="text-sm text-[#5C7083] mb-3">Distribucion de Clientes</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#4A7C59]" /><span className="text-sm">Activos</span></div>
                <span className="font-bold text-[#4A7C59]">{dirStats.active_clients}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#D98A6C]" /><span className="text-sm">Congelados</span></div>
                <span className="font-bold text-[#D98A6C]">{dirStats.frozen_clients}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#C25953]" /><span className="text-sm">Cancelados</span></div>
                <span className="font-bold text-[#C25953]">{dirStats.cancelled_clients}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dirStats?.plan_distribution?.length > 0 && (
          <div className="card" data-testid="plan-distribution-chart">
            <div className="flex items-center gap-2 mb-5">
              <PieChart className="text-[#2B4257]" size={20} />
              <h3 className="text-base font-semibold text-[#1A2835]">Planes Mas Populares</h3>
            </div>
            <div className="flex gap-4">
              <div className="w-[180px] h-[180px] flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie data={dirStats.plan_distribution.slice(0, 8)} dataKey="count" nameKey="plan" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                      {dirStats.plan_distribution.slice(0, 8).map((_, i) => (
                        <Cell key={i} fill={planColors[i % planColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v} />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto max-h-[180px]">
                {dirStats.plan_distribution.map((p, i) => (
                  <div key={p.plan} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: planColors[i % planColors.length] }} />
                      <span className="text-[#1A2835] truncate">{p.plan}</span>
                    </div>
                    <span className="font-semibold ml-2 flex-shrink-0" style={{ color: planColors[i % planColors.length] }}>{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {dirStats?.monthly_trends?.length > 0 && (
          <div className="card" data-testid="monthly-trends-chart">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 className="text-[#2B4257]" size={20} />
              <h3 className="text-base font-semibold text-[#1A2835]">Tendencia Mensual</h3>
            </div>
            <div className="flex items-center gap-4 mb-4 text-xs">
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#4A7C59]" /><span className="text-[#5C7083]">Altas</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#C25953]" /><span className="text-[#5C7083]">Bajas</span></div>
            </div>
            <div className="space-y-4">
              {dirStats.monthly_trends.map((t) => (
                <div key={t.month}>
                  <div className="flex items-center justify-between text-xs text-[#5C7083] mb-1">
                    <span>{t.month_label}</span>
                    <span className={t.neto >= 0 ? 'text-[#4A7C59] font-medium' : 'text-[#C25953] font-medium'}>
                      Neto: {t.neto >= 0 ? '+' : ''}{t.neto}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <div className="h-5 bg-[#4A7C59] rounded-sm transition-all duration-500 flex items-center justify-center text-[10px] text-white font-medium"
                      style={{ width: `${Math.max((t.altas / maxTrendVal) * 100, t.altas > 0 ? 8 : 0)}%`, minWidth: t.altas > 0 ? '24px' : '0' }}>
                      {t.altas > 0 ? t.altas : ''}
                    </div>
                    <div className="h-5 bg-[#C25953] rounded-sm transition-all duration-500 flex items-center justify-center text-[10px] text-white font-medium"
                      style={{ width: `${Math.max((t.bajas / maxTrendVal) * 100, t.bajas > 0 ? 8 : 0)}%`, minWidth: t.bajas > 0 ? '24px' : '0' }}>
                      {t.bajas > 0 ? t.bajas : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bonus & Metrics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {bonusData && (
          <div className="card" data-testid="bonus-tracking-card">
            <div className="flex items-center gap-2 mb-5">
              <Award className="text-[#D98A6C]" size={20} />
              <h3 className="text-base font-semibold text-[#1A2835]">Bonos del Mes</h3>
            </div>
            {bonusData.tracking?.length > 0 ? (
              <div className="space-y-4">
                {bonusData.tracking.map(t => (
                  <div key={t.user_id} className="p-4 border border-[#E8E2D7] rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-semibold text-[#1A2835]">{t.user_name}</p>
                      <span className="text-lg font-bold text-[#4A7C59]">{formatCurrency(t.bonus_amount)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="text-center p-2 bg-[#EDF5F0] rounded-lg">
                        <p className="text-lg font-bold text-[#4A7C59]">{t.new_clients}</p>
                        <p className="text-[10px] text-[#5C7083]">Nuevos</p>
                      </div>
                      <div className="text-center p-2 bg-[#F5F0E8] rounded-lg">
                        <p className="text-lg font-bold text-[#2B4257]">{t.renewals}</p>
                        <p className="text-[10px] text-[#5C7083]">Renovaciones</p>
                      </div>
                      <div className="text-center p-2 bg-[#FAF0EC] rounded-lg">
                        <p className="text-lg font-bold text-[#D98A6C]">{t.total_movements}</p>
                        <p className="text-[10px] text-[#5C7083]">Total</p>
                      </div>
                    </div>
                    {t.next_tier && t.remaining_for_next > 0 && (
                      <div className="text-xs text-[#5C7083] flex items-center gap-1">
                        <ArrowUpRight size={12} className="text-[#4A7C59]" />
                        <span>Faltan <span className="font-semibold text-[#2B4257]">{t.remaining_for_next}</span> para el siguiente tramo ({formatCurrency(t.next_tier.amount)})</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5C7083] text-center py-6">No hay datos de bonos para este mes</p>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div className="card" data-testid="client-metrics-card">
            <h3 className="text-base font-semibold text-[#1A2835] mb-4">Metricas de Clientes</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-[#EDF5F0] rounded-lg cursor-pointer hover:bg-[#dceee2] transition-colors"
                onClick={() => navigate('/clientes')} data-testid="metric-new-clients">
                <div className="flex items-center gap-2"><Users className="text-[#4A7C59]" size={18} /><span className="text-sm text-[#1A2835]">Nuevos Clientes</span></div>
                <span className="text-xl font-bold text-[#4A7C59]">{stats?.new_clients_month || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#FBEAE9] rounded-lg cursor-pointer hover:bg-[#f5d5d3] transition-colors"
                onClick={() => navigate('/casos-operativos')} data-testid="metric-cancellations">
                <div className="flex items-center gap-2"><TrendingDown className="text-[#C25953]" size={18} /><span className="text-sm text-[#1A2835]">Cancelaciones</span></div>
                <span className="text-xl font-bold text-[#C25953]">{stats?.cancellations_month || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#FAF0EC] rounded-lg cursor-pointer hover:bg-[#f0ddd5] transition-colors"
                onClick={() => navigate('/pagos-rechazados')} data-testid="metric-rejected-payments">
                <div className="flex items-center gap-2"><CreditCard className="text-[#D98A6C]" size={18} /><span className="text-sm text-[#1A2835]">Pagos Rechazados</span></div>
                <span className="text-xl font-bold text-[#D98A6C]">{stats?.rejected_payments || 0}</span>
              </div>
            </div>
          </div>

          {lowStockProducts.length > 0 && (
            <div className="card" data-testid="low-stock-card">
              <div className="flex items-center gap-2 mb-3"><Package className="text-[#D98A6C]" size={18} /><h3 className="text-base font-semibold text-[#1A2835]">Stock Bajo</h3></div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Producto</th><th>Stock</th><th>Min</th></tr></thead>
                  <tbody>
                    {lowStockProducts.map(p => (
                      <tr key={p.id}>
                        <td className="font-medium text-sm">{p.name}</td>
                        <td><span className={p.stock <= p.min_stock ? 'text-[#C25953] font-semibold' : ''}>{p.stock}</span></td>
                        <td>{p.min_stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Cases */}
      <div className="card" data-testid="recent-movements-card">
        <h3 className="text-base font-semibold text-[#1A2835] mb-4">Ultimos Casos Operativos</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Tipo</th><th>Cliente</th><th>Plan</th><th>Origen</th><th>Fecha</th></tr></thead>
            <tbody>
              {movements.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-[#5C7083]">No hay casos recientes</td></tr>
              ) : (
                movements.map(m => (
                  <tr key={m.id}>
                    <td><span className={`status-badge ${m.tipo_accion === 'nuevo_cliente' ? 'status-activo' : m.tipo_accion === 'cancelacion' ? 'status-cancelado' : 'status-pendiente'}`}>{movementTypeLabels[m.tipo_accion] || m.tipo_accion}</span></td>
                    <td className="font-medium">{m.client_name || '-'}</td>
                    <td>{m.plan || '-'}</td>
                    <td>{m.origen || '-'}</td>
                    <td>{m.fecha_accion || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardDireccion;
